import React, {useEffect, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Flex,
    Heading,
    Stack,
    useBreakpointValue,
    useMultiStyleConfig
} from '@chakra-ui/react'
import DisplayPrice from '../display-price'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import DynamicImage from '@app/components/dynamic-image'
import {getPriceData} from '../../utils/product-utils'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {useShopperBasketsMutationHelper, useProducts} from '@salesforce/commerce-sdk-react'
import {useIntl, FormattedMessage} from 'react-intl'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '@app/hooks/use-toast'
import {useGTM} from '@app/hooks/use-gtm'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import {useLocation} from 'react-router-dom'
import Link from '../link'
import {productUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import SwatchGroup from '../swatch-group'

const ShopLookDrawerButton = ({onClick, productId, onMoreDetailsClick, isDisabled}) => (
    <Stack textAlign={'center'} spacing={10}>
        <Button key="cart-button" onClick={onClick} disabled={isDisabled} variant="primary">
            <FormattedMessage defaultMessage="Add to Cart" id="product_view.button.add_to_cart" />
        </Button>
        <Link onClick={onMoreDetailsClick} to={productUrlBuilder({id: productId})}>
            <FormattedMessage defaultMessage="More details" id="product_view.link.more_details" />
        </Link>
    </Stack>
)

ShopLookDrawerButton.propTypes = {
    onClick: PropTypes.func,
    productId: PropTypes.string,
    onMoreDetailsClick: PropTypes.func,
    isDisabled: PropTypes.bool
}

const ShopLookDrawerProducts = ({products, onClose, styles, activeCurrency}) => {
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const intl = useIntl()
    const [selectedVariants, setSelectedVariants] = useState({})
    const showToast = useToast()
    const location = useLocation()
    const {
        onOpen: onAddToCartModalOpen,
        onClose: onAddToCartModalClose,
        isOpen: isAddToCartModalOpen
    } = useAddToCartModalContext()
    const {triggerAddToCartEvent} = useGTM()

    useEffect(() => {
        if (isAddToCartModalOpen) onAddToCartModalClose()
    }, [location.pathname])

    const productIds = products?.map((product) => product.productId || product.id)
    const {data: productSearchResult} = useProducts(
        {
            parameters: {
                ids: productIds?.join(','),
                allImages: true,
                select: '(data.(id, variationAttributes.(**), imageGroups.(**)))'
            }
        },
        {enabled: !!productIds?.length}
    )

    const processedProducts = useMemo(() => {
        if (!products) return []
        return products.map((product) => {
            const apiData = productSearchResult?.data?.find(
                (p) => p.id === (product.productId || product.id)
            )
            const updatedProduct = {
                ...product,
                variationAttributes: apiData?.variationAttributes || product.variationAttributes,
                imageGroups: apiData?.imageGroups || product.imageGroups
            }
            if (updatedProduct.variationAttributes) {
                const sizeAttr = updatedProduct.variationAttributes.find(
                    (attr) => attr.id === 'size'
                )
                if (sizeAttr && updatedProduct.variants) {
                    const sizeOrder = sizeAttr.values.map((v) => v.value)
                    updatedProduct.variants.sort(
                        (a, b) =>
                            sizeOrder.indexOf(a.variationValues.size) -
                            sizeOrder.indexOf(b.variationValues.size)
                    )
                }
            }
            return updatedProduct
        })
    }, [products, productSearchResult])

    const handleChangeVariant = (productId, variant) => {
        setSelectedVariants((prev) => ({...prev, [productId]: variant}))
    }

    const SizeSwatch = ({item, isSelected, onClick, isFocusable}) => {
        const swatchStyles = useMultiStyleConfig('SwatchGroup', {
            variant: 'size',
            disabled: !item.orderable,
            selected: isSelected
        })
        return (
            <Button
                type="button"
                role="radio"
                aria-checked={isSelected}
                tabIndex={isFocusable ? 0 : -1}
                onClick={onClick}
                {...swatchStyles.swatch}
            >
                {item.variationValues.size}
            </Button>
        )
    }

    /**
     * Handles the addition of a product to the shopping cart.
     *
     * This asynchronous function attempts to add a specified product variant to the user's
     * shopping cart. If the variant is available, it constructs the necessary product items
     * and items added arrays, and then calls the function to add these items to a new or
     * existing basket. Upon successful addition, it closes the current modal and opens a
     * modal indicating the item has been added to the cart. If an error occurs during this
     * process, it displays an error toast message to the user.
     *
     * If no variant is provided, it sets a flag to show a message indicating that options
     * need to be selected.
     *
     * @async
     * @param {Object} product - The product object to be added to the cart.
     * @param {Object} product.variant - The variant of the product to be added.
     * @param {string} product.variant.productId - The unique identifier for the product variant.
     * @param {number} product.variant.price - The price of the product variant.
     * @param {Function} addItemToNewOrExistingBasket - Function to add items to the basket.
     * @param {Function} onClose - Function to close the current modal.
     * @param {Function} onAddToCartModalOpen - Function to open the add-to-cart modal.
     * @param {Function} showToast - Function to display toast messages.
     * @param {Object} intl - Internationalization object for formatting messages.
     * @param {Object} API_ERROR_MESSAGE - The error message to display in case of API failure.
     */
    const handleCartItem = async (product) => {
        const productId = product.id || product.productId
        const variant = selectedVariants[productId]
        if (variant) {
            const productItems = [
                {
                    productId: variant.productId,
                    price: variant.price,
                    quantity: 1,
                    c_origin: 'shop_the_look'
                }
            ]

            const itemsAdded = [
                {
                    product,
                    variant,
                    quantity: 1
                }
            ]

            try {
                const basketResponse = await addItemToNewOrExistingBasket(productItems)
                onClose()
                onAddToCartModalOpen({
                    product,
                    itemsAdded,
                    selectedQuantity: 1
                })

                // GTM Integration
                const finalProduct = {
                    ...product,
                    ...variant
                }
                finalProduct.ean = variant?.ean || variant?.productId
                triggerAddToCartEvent(
                    basketResponse?.productItems,
                    finalProduct,
                    1,
                    'shop_the_look'
                )
            } catch (e) {
                showToast({
                    title: intl.formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            }
        }
    }

    return (
        <Stack spacing={20}>
            {processedProducts.map((product) => {
                const productId = product.id || product.productId
                const variants = product.variants || []
                const firstOrderable = variants.find((v) => v.orderable)
                const activeVariant = selectedVariants[productId] || firstOrderable
                const image =
                    findImageGroupBy(product.imageGroups, {
                        viewType: 'front',
                        selectedVariationAttributes:
                            activeVariant?.variationValues || product.variationValues
                    })?.images?.[0] || product.image

                return (
                    <Stack spacing={10} key={`prod-stack-${productId}`}>
                        <Flex gap={20}>
                            <Link to={productUrlBuilder({id: productId})}>
                                {image && (
                                    <DynamicImage
                                        width={isMobile ? '125px' : '180px'}
                                        src={`${image?.disBaseLink || image?.link}[?sw={width}]`}
                                        widths={[85, 147, 170, 294]}
                                        imageProps={{alt: image?.alt, loading: 'lazy'}}
                                    />
                                )}
                            </Link>
                            <Box width="100%">
                                <Heading as={'div'} {...styles.productName}>
                                    <Link
                                        to={productUrlBuilder({id: productId})}
                                        textDecoration="none"
                                    >
                                        {product.name || product.productName}
                                    </Link>
                                </Heading>
                                <DisplayPrice
                                    priceData={getPriceData(product)}
                                    currency={product?.currency || activeCurrency}
                                    {...styles.productPrice}
                                />
                                <Box p="24px 0">
                                    <SwatchGroup
                                        variant="size"
                                        value={activeVariant?.productId}
                                        handleChange={(val) => {
                                            const v = variants.find(v => v.productId === val)
                                            if (v) handleChangeVariant(productId, v)
                                        }}
                                    >
                                        {variants.map((item) => {
                                            return (
                                                <SizeSwatch
                                                    key={`${productId}-${item.productId}`}
                                                    item={item}
                                                    value={item.productId}
                                                    isSelected={
                                                        activeVariant?.productId === item.productId
                                                    }
                                                    onClick={() =>
                                                        handleChangeVariant(productId, item)
                                                    }
                                                />
                                            )
                                        })}
                                    </SwatchGroup>
                                </Box>
                                <HideOnMobile>
                                    <ShopLookDrawerButton
                                        onClick={() => handleCartItem(product, activeVariant)}
                                        productId={productId}
                                        isDisabled={!activeVariant?.orderable}
                                    />
                                </HideOnMobile>
                            </Box>
                        </Flex>
                        <HideOnDesktop>
                            <ShopLookDrawerButton
                                onClick={() => handleCartItem(product, activeVariant)}
                                productId={productId}
                                isDisabled={!activeVariant?.orderable}
                            />
                        </HideOnDesktop>
                    </Stack>
                )
            })}
        </Stack>
    )
}

ShopLookDrawerProducts.propTypes = {
    products: PropTypes.array,
    onClose: PropTypes.func,
    styles: PropTypes.object,
    activeCurrency: PropTypes.string
}

export default ShopLookDrawerProducts
